import { config } from '../config';
import { log } from './logger';
import { errorMessage } from './errors';

/**
 * Design §8.2's playability probe — *"an `HTMLAudioElement.canplaythrough` on the assembled blob,
 * bounded by a timeout"*.
 *
 * ── What this answers, and what it must never be read as ────────────────────
 *
 * §8.2: *"A dangling or incomplete blob is never presented as a normal note. On finalize, a
 * playability probe … marks unplayable segments; they are listed as 'audio unavailable — flagged at
 * 34′', which is the same honest degradation as FLAG-4's silent flag and preserves D-19's
 * 'something happened at minute 34' value."*
 *
 * So the probe's job is **labelling**, never gating. It cannot delete audio, cannot stop a recording
 * finalizing, and cannot change what is on disk — AUD-1 forbids transcoding on any path, so a blob
 * that will not play stays exactly the bytes the recorder emitted. The worst an unplayable verdict
 * does is change a caption.
 *
 * ── Three signals, not one ──────────────────────────────────────────────────
 *
 * `canPlayThrough` is the strict answer. A fragmented MP4 missing its final fragment can parse far
 * enough to yield `loadedmetadata` and a duration while never reaching `canplaythrough`, and those
 * two outcomes mean different things: *"the container is broken"* versus *"the browser would not
 * commit"*. Reporting only the strict flag collapses them.
 *
 * The probe is also capable of a **false negative** — iOS is conservative about loading media
 * without user activation, so a timeout does not prove a file is broken. {@link isPlayable}
 * therefore reads the *whole* result rather than one flag, and `timedOut` is kept distinct from
 * `mediaErrorCode` so a caller can tell "we could not ask" from "the answer is no".
 *
 * ── Derived, never persisted — a U-11 decision, declared ────────────────────
 *
 * There is no playability column on `AudioBlob`, and this unit does not add one. Adding it would be
 * a `v003` migration (U-3's schema) *and* a change to the archive's field list (U-5, INV-12's byte
 * determinism) — and it would be **wrong**: playability is a property of the engine doing the
 * asking, not of the bytes. A `playable: false` written by one device would ride a backup onto
 * another and mislabel a recording that plays there perfectly well. So the verdict is computed at
 * finalize (where §8.2 asks for it) and recomputable at read time by whichever surface is listing.
 *
 * ── BUILD-UNIT NOTE ─────────────────────────────────────────────────────────
 *
 * U-1 wrote this as `harness/playability.ts` and declared it *"a thin slice of U-11's probe"*. This
 * is that probe. The harness file now delegates here and adds only its per-variant reporting
 * wrapper, so there is one implementation of the container question and the spike scored segments
 * with the same code the product labels them with.
 */

const MODULE = 'playability';

/** The DOM surface the probe uses, named so it can be faked in Node. */
export interface AudioElementLike {
  src: string;
  preload: string;
  duration: number;
  readyState: number;
  error: { code: number; message?: string } | null;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
  load(): void;
}

export interface ProbeDeps {
  createAudio(): AudioElementLike;
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
  now(): number;
}

/**
 * The browser implementation.
 *
 * **Building the deps never throws; using them can.** The `Audio` and `URL` lookups are inside the
 * closures, so an environment without them fails at the moment {@link probePlayability} calls one —
 * where it is caught and recorded as the result's `error`. That is the shape the product needs:
 * "we could not ask" and "the answer is no" must not read the same, and a caller must never have to
 * decide which of the two a thrown exception was.
 */
export function browserProbeDeps(): ProbeDeps {
  const g = globalThis as unknown as {
    Audio?: new () => AudioElementLike;
    URL?: { createObjectURL(b: Blob): string; revokeObjectURL(u: string): void };
    performance?: { now(): number };
  };
  return {
    createAudio() {
      if (typeof g.Audio !== 'function') {
        throw new Error('No Audio constructor in this environment.');
      }
      return new g.Audio();
    },
    createObjectURL(blob) {
      if (!g.URL) throw new Error('No URL.createObjectURL in this environment.');
      return g.URL.createObjectURL(blob);
    },
    revokeObjectURL(url) {
      g.URL?.revokeObjectURL(url);
    },
    now() {
      return g.performance?.now() ?? Date.now();
    },
  };
}

/** One probe's answer, with the reason it could not answer kept separate from a negative answer. */
export interface PlayabilityProbe {
  canPlayThrough: boolean;
  metadataLoaded: boolean;
  durationSeconds: number | null;
  /** `HTMLMediaElement.error.code`, when the element reported one. */
  mediaErrorCode: number | null;
  mediaErrorMessage: string | null;
  probeMs: number;
  timedOut: boolean;
  /** Populated when the probe itself could not run (no `Audio` constructor, object URL refused). */
  error: string | null;
}

/**
 * Asks the browser to play the blob through, bounded by `PLAYABILITY_PROBE_TIMEOUT_MS`
 * (design §13.1: *"3 s, no retry — treat as unplayable"*).
 *
 * **Never throws.** A probe that cannot run is a probe result carrying an `error`. This is
 * load-bearing rather than defensive: the probe runs inside `finalizeRecording`, and AUD-3 says
 * captured audio is never discarded — an exception here would strand a recording that is already
 * on disk behind a failure to describe it.
 */
export async function probePlayability(
  blob: Blob,
  mime: string,
  deps: ProbeDeps = browserProbeDeps(),
  timeoutMs: number = config.PLAYABILITY_PROBE_TIMEOUT_MS,
): Promise<PlayabilityProbe> {
  const base: PlayabilityProbe = {
    canPlayThrough: false,
    metadataLoaded: false,
    durationSeconds: null,
    mediaErrorCode: null,
    mediaErrorMessage: null,
    probeMs: 0,
    timedOut: false,
    error: null,
  };

  if (blob.size === 0) {
    return {
      ...base,
      error:
        'The assembled blob is empty — there was nothing to probe. This is a missing-chunk problem, not a container problem.',
    };
  }

  let audio: AudioElementLike;
  let url: string;
  try {
    audio = deps.createAudio();
    url = deps.createObjectURL(blob);
  } catch (cause) {
    return { ...base, error: `Could not start the probe: ${errorMessage(cause)}` };
  }

  const t0 = deps.now();
  const result: PlayabilityProbe = { ...base };

  await new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== null) clearTimeout(timer);
      audio.removeEventListener('canplaythrough', onCanPlay);
      audio.removeEventListener('loadedmetadata', onMetadata);
      audio.removeEventListener('error', onError);
      resolve();
    };

    function captureDuration(): void {
      const d = audio.duration;
      // A fragmented file with no `moov` commonly reports `Infinity` or `NaN`. Recording the raw
      // value would put a non-finite number in the export and break the stats; recording `null`
      // and the flag separately keeps both facts.
      result.durationSeconds = Number.isFinite(d) && d > 0 ? d : null;
    }

    function onMetadata(): void {
      result.metadataLoaded = true;
      captureDuration();
    }

    function onCanPlay(): void {
      result.canPlayThrough = true;
      captureDuration();
      finish();
    }

    function onError(): void {
      result.mediaErrorCode = audio.error?.code ?? null;
      result.mediaErrorMessage = audio.error?.message ?? null;
      finish();
    }

    audio.addEventListener('canplaythrough', onCanPlay);
    audio.addEventListener('loadedmetadata', onMetadata);
    audio.addEventListener('error', onError);

    timer = setTimeout(() => {
      result.timedOut = true;
      finish();
    }, timeoutMs);

    audio.preload = 'auto';
    audio.src = url;
    try {
      audio.load();
    } catch (cause) {
      result.error = `load() threw: ${errorMessage(cause)}`;
      finish();
    }
  });

  result.probeMs = deps.now() - t0;

  // Detach the source before revoking, so the element cannot hold a URL that no longer resolves.
  try {
    audio.src = '';
    deps.revokeObjectURL(url);
  } catch (cause) {
    log.warn(MODULE, 'object URL cleanup failed', { error: errorMessage(cause) });
  }

  log.info(MODULE, 'probe complete', {
    bytes: blob.size,
    mime,
    canPlayThrough: result.canPlayThrough,
    metadataLoaded: result.metadataLoaded,
    durationSeconds: result.durationSeconds,
    mediaErrorCode: result.mediaErrorCode,
    timedOut: result.timedOut,
  });

  return result;
}

/**
 * §8.2's caption test — **does this get presented as a normal note?**
 *
 * `canPlayThrough` and nothing else. The two soft outcomes are deliberately *not* treated as
 * playable:
 *
 * - a **timeout** may be iOS declining to load without user activation, which is a false negative —
 *   but §8.2's degradation is *"audio unavailable — flagged at 34′"*, which stays honest under a
 *   false negative (the flag, its minute and its bytes all survive) and would be a lie under a false
 *   positive (a note offered for playback that produces silence).
 * - **metadata only** is the truncated-MP4 case §8.5 names by hand. It parsed; it will not play.
 *
 * The asymmetry is the point: an over-cautious caption costs a caption, an over-confident one costs
 * the user's trust in the one thing they cannot re-derive after the match.
 */
export function isPlayable(probe: PlayabilityProbe): boolean {
  return probe.canPlayThrough;
}
